import { enqueueSnackbar, DownloadFile } from 'actions';
import { ProcessMocnWorkRequest, GenerateAuditReportRequest, ProcessEnodebPairingWorkRequest } from '../requests/ScriptsGenerationRequests';

// Action Types
export const SET_FORM_STATE = 'SET_FORM_STATE';
export const RESET_FIELDS = 'RESET_FIELDS';
export const GET_CLUSTERS_INFO = 'GET_CLUSTERS_INFO';
export const SET_LOADING = 'SET_LOADING';
export const SET_PROCESSING_RESULT = 'SET_PROCESSING_RESULT';
export const SET_ENODEB_PAIRING_LOADING = 'SET_ENODEB_PAIRING_LOADING';
export const SET_ENODEB_PAIRING_RESULT = 'SET_ENODEB_PAIRING_RESULT';

// Action Creators
export const SetFormState = (formState) => ({
	type: SET_FORM_STATE,
	formState
});

export const ResetFields = () => ({
	type: RESET_FIELDS
});

export const GetClustersInfo = (clustersInfo) => ({
	type: GET_CLUSTERS_INFO,
	clustersInfo
});

export const SetLoading = (isLoading) => ({
	type: SET_LOADING,
	isLoading
});

export const SetProcessingResult = (result) => ({
	type: SET_PROCESSING_RESULT,
	result
});

export const SetEnodebPairingLoading = (isLoading) => ({
	type: SET_ENODEB_PAIRING_LOADING,
	isLoading
});

export const SetEnodebPairingResult = (result) => ({
	type: SET_ENODEB_PAIRING_RESULT,
	result
});

// Async Action for Processing MOCN Work
export const ProcessMocnWork = (request, excelFile = null) => {
	return async (dispatch) => {
		try {
			dispatch(SetLoading(true));
			dispatch(SetProcessingResult(null));

			// Create FormData with CORRECT C# PascalCase property names
			const formData = new FormData();
			formData.append('DeploymentDate', request.deploymentDate);
			formData.append('UseDatabase', request.useDatabase.toString());
			
			if (request.scfFolderPath) {
				formData.append('ScfFolderPath', request.scfFolderPath);
			}
			
			// Add Excel file if provided
			if (excelFile) {
				formData.append('excelFile', excelFile);
			}

			// Call ProcessMocnWork endpoint
			const response = await ProcessMocnWorkRequest(formData);

			if (response.status === 200) {
				dispatch(SetProcessingResult(response.data));
				
				// Check if response contains file data for download
				if (response.data.zipFilePath) {
					dispatch(DownloadFile(response.data.zipFilePath));
				} else {
					dispatch(enqueueSnackbar({
						message: `Successfully processed ${response.data.processedSites || 0} Files`,
						options: { variant: 'success' }
					}));
				}
			} else {
				const errorMessage = response.data?.errorMessage || response.data?.error || 'Processing failed';
				dispatch(SetProcessingResult({
					success: false,
					errorMessage: errorMessage
				}));
				dispatch(enqueueSnackbar({
					message: errorMessage,
					options: { variant: 'error' }
				}));
			}
		} catch (error) {
			// Try to extract the most specific error message
			let errorMessage = 'Network error!';
			let fullResult = null;
			
			if (error.response?.data) {
				// If backend returned the full MocnBatchResult with error
				if (error.response.data.success === false && error.response.data.error) {
					errorMessage = error.response.data.error;
					fullResult = error.response.data; // Keep the full result for display
				} else if (typeof error.response.data === 'string') {
					errorMessage = error.response.data;
				} else if (error.response.data.error) {
					errorMessage = error.response.data.error;
				} else if (error.response.data.errorMessage) {
					errorMessage = error.response.data.errorMessage;
				} else if (error.response.data.message) {
					errorMessage = error.response.data.message;
				} else if (error.response.data.title) {
					errorMessage = error.response.data.title;
					if (error.response.data.errors) {
						const validationErrors = Object.entries(error.response.data.errors)
							.map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
							.join('; ');
						errorMessage += ` - ${validationErrors}`;
					}
				} else {
					errorMessage = JSON.stringify(error.response.data);
				}
			} else if (error.message) {
				errorMessage = error.message;
			}
			
			// If we have a full result object, use it; otherwise create a minimal one
			const resultToDispatch = fullResult || {
				success: false,
				errorMessage: errorMessage
			};
			
			dispatch(SetProcessingResult({
				success: false,
				errorMessage: errorMessage
			}));
			
			dispatch(enqueueSnackbar({
				message: errorMessage,
				options: { variant: 'error' }
			}));
		} finally {
			dispatch(SetLoading(false));
		}
	};
};

// Async Action for Generating Audit Report
export const GenerateAuditReport = () => {
	return async (dispatch) => {
		try {
			dispatch(SetLoading(true));
			dispatch(SetProcessingResult(null));

			const response = await GenerateAuditReportRequest();

			if (response.status === 200) {
				dispatch(SetProcessingResult({
					success: true,
					totalSites: response.data.summary?.processedSites || 0,
					processedSites: response.data.summary?.processedSites || 0,
					failedSites: response.data.summary?.failedSites || 0,
					generatedFiles: response.data.summary?.generatedFiles || [],
					auditSummary: response.data.summary?.auditSummary || null,
					downloadFileName: response.data.excelReportPath ? response.data.excelReportPath : null
				}));
				
				// Check if response contains file data for download
				if (response.data.excelReportPath) {
					dispatch(DownloadFile(response.data.excelReportPath));
				} else {
					dispatch(enqueueSnackbar({
						message: 'Audit report generated successfully',
						options: { variant: 'success' }
					}));
				}
			} else {
				const errorMessage = response.data?.error || 'Audit report generation failed';
				dispatch(SetProcessingResult({
					success: false,
					errorMessage: errorMessage
				}));
				dispatch(enqueueSnackbar({
					message: errorMessage,
					options: { variant: 'error' }
				}));
			}
		} catch (error) {
			console.error('Audit generation error:', error);
			
			const errorMessage = error.response?.data?.error || 
			                    error.response?.data?.message ||
			                    error.message || 
			                    'Network error!';
			
			dispatch(SetProcessingResult({
				success: false,
				errorMessage: errorMessage
			}));
			
			dispatch(enqueueSnackbar({
				message: errorMessage,
				options: { variant: 'error' }
			}));
		} finally {
			dispatch(SetLoading(false));
		}
	};
};

// Async Action for Processing EnodebPairing Work
export const ProcessEnodebPairingWork = (request, excelFile) => {
	return async (dispatch) => {
		try {
			dispatch(SetEnodebPairingLoading(true));
			dispatch(SetEnodebPairingResult(null));

			// Create FormData for file upload (matching MOCN pattern)
			const formData = new FormData();
			formData.append('deploymentDate', request.deploymentDate);
			formData.append('useDatabase', request.useDatabase.toString());
			
			if (request.scfFolderPath) {
				formData.append('scfFolderPath', request.scfFolderPath);
			}
			
			// Add Excel file if provided
			if (excelFile) {
				formData.append('excelFile', excelFile);
			}

			const response = await ProcessEnodebPairingWorkRequest(formData);

			if (response.status === 200) {
				dispatch(SetEnodebPairingResult({
					success: true,
					totalSites: response.data.totalSites || 0,
					processedSites: response.data.processedSites || 0,
					failedSites: response.data.failedSites || 0,
					siteResults: response.data.siteResults || [],
					zipFilePath: response.data.zipFilePath,
					deploymentDate: response.data.deploymentDate,
					processedAt: response.data.processedAt
				}));

				// Auto-download ZIP if available
				if (response.data.zipFilePath) {
					dispatch(DownloadFile(response.data.zipFilePath));
				}

				dispatch(enqueueSnackbar({
					message: response.data.message || `EnodebPairing completed successfully. Processed ${response.data.processedSites || 0} || 0} files.`,
					options: { variant: 'success' }
				}));
			} else {
				const errorMessage = response.data?.error || 'EnodebPairing processing failed';
				dispatch(SetEnodebPairingResult({
					success: false,
					errorMessage: errorMessage
				}));
				dispatch(enqueueSnackbar({
					message: errorMessage,
					options: { variant: 'error' }
				}));
			}
		} catch (error) {
			console.error('EnodebPairing processing error:', error);
			
			const errorMessage = error?.response?.data?.error || 
			                    error?.response?.data?.message ||
			                    error?.message || 
			                    'Network error during EnodebPairing processing!';
			
			dispatch(SetEnodebPairingResult({
				success: false,
				errorMessage: errorMessage
			}));
			
			dispatch(enqueueSnackbar({
				message: errorMessage,
				options: { variant: 'error' }
			}));
		} finally {
			dispatch(SetEnodebPairingLoading(false));
		}
	};
};